import { Grid, pn, Switch, Tooltip, Typography, useElementBreakpoints } from "@compass/core";
import { Select, StudentCustomIdsPaster } from "components";
import {
  LkStudentDisplayFieldId,
  LkStudentDisplayFilterId,
  LkStudentEnrollmentDisplayFieldId,
  ReportPropertyBase,
  StudentFilter as StudentFilterModel,
  StudentFilterResource,
  StudentTextingOptInFilterId,
} from "generated/openApi";
import produce from "immer";
import { ReactNode, useEffect, useState } from "react";
import HelpIcon from "@mui/icons-material/Help";

export const StudentDemographicsFilterAccordion = (props: {
  resource: StudentFilterResource;
  filter: StudentFilterModel;
  setFilter: (filter: StudentFilterModel) => void;
  showActiveSwitch?: boolean;
  showHideArchived?: boolean;
  activeSchoolYearLabel?: string;
  contentAfterActiveSwitches?: ReactNode;
}) => {
  const { fullFieldBreakpoints } = useElementBreakpoints();
  const properties = props.resource.properties || [];
  const displayFields = props.resource.enrollmentDisplayFields || [];

  const gradeLevelDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.GradeLevel
  );
  const schoolDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.School
  );
  const archivedDisplayField = props.resource.displayFields?.find(
    (x) => x.id === LkStudentDisplayFieldId.Archived
  );

  const [isActiveSelected, setIsActiveSelected] = useState<boolean>(false);

  const activeSchoolYear = props.resource.schoolYearsWithActive?.find((x) =>
    x.name?.toLowerCase().includes("active")
  );

  const activeSchoolYearId = activeSchoolYear?.id;

  const activeTooltip = activeSchoolYear
    ? `${activeSchoolYear.name.replace(" (Active)", "")}`
    : "No active school year found";

  const activeSchoolYearTooltip = activeSchoolYear
    ? activeSchoolYear.name.replace(/\s*and active$/i, "")
    : "No active school year found";

  const textingOptInFilterOptions = [
    { id: StudentTextingOptInFilterId.StudentOptedOut, name: "Students opted out of texting" },
    { id: StudentTextingOptInFilterId.StudentOptedIn, name: "Students opted in to texting" },
    { id: StudentTextingOptInFilterId.ParentOptedOut, name: "Parents opted out of texting" },
    { id: StudentTextingOptInFilterId.ParentOptedIn, name: "Parents opted in to texting" },
  ];

  const hasActiveSelected = (): boolean => {
    if (!activeSchoolYearId) return false;
    return props.filter.schoolYears?.includes(activeSchoolYearId) || false;
  };

  useEffect(() => {
    setIsActiveSelected(hasActiveSelected());
  }, [props.filter.schoolYears, activeSchoolYearId]);

  const schoolYearField = () => {
    const propertyName = pn<StudentFilterModel>("schoolYears");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.schoolYearsWithActive?.filter((x) =>
          props.filter.schoolYears?.includes(x.id)
        )}
        options={props.resource.schoolYearsWithActive}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolYears = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const regionField = () => {
    const propertyName = pn<StudentFilterModel>("regionIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.regions?.filter((x) =>
          props.filter.regionIds?.includes(x.id)
        )}
        options={props.resource.regions}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.regionIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const districtField = () => {
    const propertyName = pn<StudentFilterModel>("districtIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.districts?.filter((x) =>
          props.filter.districtIds?.includes(x.id)
        )}
        options={props.resource.districts}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.districtIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentFilterModel>("schoolIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        label={schoolDisplayField?.displayName}
        multiple
        modelProperty={property}
        value={props.resource.schools?.filter((x) =>
          props.filter.schoolIds?.includes(x.id)
        )}
        options={props.resource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const gradeLevelsField = () => {
    const propertyName = pn<StudentFilterModel>("gradeLevelIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        label={gradeLevelDisplayField?.displayName}
        multiple
        modelProperty={property}
        value={props.resource.gradeLevels?.filter((x) =>
          props.filter.gradeLevelIds?.includes(x.id)
        )}
        options={props.resource.gradeLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.gradeLevelIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const studentGroupsField = () => {
    const propertyName = pn<StudentFilterModel>("studentGroupIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.studentGroups?.filter((x) =>
          props.filter.studentGroupIds?.includes(x.id)
        )}
        options={props.resource.studentGroups}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.studentGroupIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const duplicateByField = () => {
    const propertyName = pn<StudentFilterModel>("duplicateByProperties");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.duplicateByProperties?.filter((x) =>
          props.filter.duplicateByProperties?.includes(x.propertyName)
        )}
        options={props.resource.duplicateByProperties}
        getOptionLabel={(option) => option.displayName}
        onChange={(e, values: ReportPropertyBase[]) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.duplicateByProperties = values.map(
                (x) => x.propertyName
              );
            })
          )
        }
      />
    );
  };

  const programField = () => {
    const propertyName = pn<StudentFilterModel>("programIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={props.resource.programs?.filter((x) =>
          props.filter.programIds?.includes(x.id)
        )}
        options={props.resource.programs}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.programIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const textingOptInField = () => {
    const propertyName = pn<StudentFilterModel>("textingOptInFilterIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={textingOptInFilterOptions.filter((x) => props.filter.textingOptInFilterIds?.includes(x.id))}
        options={textingOptInFilterOptions}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.textingOptInFilterIds = values.map((x) => x.id);
              draft.textingOptInFilterId = undefined;
            })
          )
        }
      />
    );
  };

  // const serviceCountOperators = [
  //   { id: "eq", name: "Equal to" },
  //   { id: "gt", name: "Greater than" },
  //   { id: "gte", name: "Greater than or equal to" },
  //   { id: "lt", name: "Less than" },
  //   { id: "lte", name: "Less than or equal to" },
  // ];

  // const serviceCountOptions = Array.from({ length: 11 }, (_, i) => ({
  //   id: i,
  //   name: i === 10 ? "10" : i.toString(),
  // }));

  // const serviceCountField = () => {
  //   const propertyName = pn<StudentFilterModel>("serviceCounts");
  //   const property = properties.find((x) => x.propertyName === propertyName);

  //   return (
  //     <div style={{ display: "flex", gap: 12 }}>
  //       <Select
  //         label="Service Count"
  //         value={serviceCountOptions.find(
  //           (x) => x.id === props.filter.serviceCounts?.value
  //         )}
  //         options={serviceCountOptions}
  //         getOptionLabel={(option) => option.name}
  //         onChange={(e, value) =>
  //           props.setFilter(
  //             produce(props.filter, (draft) => {
  //               draft.serviceCounts = {
  //                 ...(draft.serviceCounts ?? { value: 0, operator: "eq" }),
  //                 value: value?.id ?? 0,
  //               };
  //             })
  //           )
  //         }
  //       />
  //       <Select
  //         label="Condition"
  //         value={serviceCountOperators.find(
  //           (x) => x.id === props.filter.serviceCounts?.operator
  //         )}
  //         options={serviceCountOperators}
  //         getOptionLabel={(option) => option.name}
  //         onChange={(e, value) =>
  //           props.setFilter(
  //             produce(props.filter, (draft) => {
  //               draft.serviceCounts = {
  //                 ...(draft.serviceCounts ?? { value: 0, operator: "eq" }),
  //                 operator: value?.id ?? "eq",
  //               };
  //             })
  //           )
  //         }
  //       />
  //     </div>
  //   );
  // };

  return (
    <Grid container spacing="sm">
      {props.showActiveSwitch && (
        <Grid
          item
          xs={fullFieldBreakpoints}
          display="flex"
          flexDirection="row"
          flexWrap="nowrap"
          alignItems="center"
          justifyContent="space-between"
          gap={2}>
          <Tooltip title={activeTooltip} arrow>
            <div>
              <Switch
                color="primary"
                checked={props.filter.activelyEnrolledStudentsOnly}
                label="Actively Enrolled Students"
                onChange={(e, checked) => {
                  props.setFilter(
                    produce(props.filter, (draft) => {
                      draft.activelyEnrolledStudentsOnly = checked;
                    })
                  );
                }}
              />
            </div>
          </Tooltip>
        </Grid>
      )}

      <Grid item xs={fullFieldBreakpoints}>
        <Tooltip title={activeSchoolYearTooltip} arrow>
          <div>
            <Switch
              color="success"
              checked={isActiveSelected}
              label={props.showActiveSwitch ? "Current Enrollment Year Only" : "Actively Enrolled Students"}
              onChange={(e, checked) => {
                setIsActiveSelected(checked);

                props.setFilter(
                  produce(props.filter, (draft) => {
                    if (!activeSchoolYearId) return;

                    if (checked) {
                      draft.schoolYears = draft.schoolYears || [];

                      if (!draft.schoolYears.includes(activeSchoolYearId)) {
                        draft.schoolYears.push(activeSchoolYearId);
                      }
                    } else {
                      draft.schoolYears =
                        draft.schoolYears?.filter(
                          (id) => id !== activeSchoolYearId
                        ) ?? [];
                    }
                  })
                );
              }}
            />
          </div>
        </Tooltip>
      </Grid>

      {props.contentAfterActiveSwitches && (
        <Grid item xs={fullFieldBreakpoints}>
          {props.contentAfterActiveSwitches}
        </Grid>
      )}

      {props.showHideArchived && archivedDisplayField && (
        <Grid item xs={fullFieldBreakpoints}>
          <Switch
            color="success"
            checked={props.filter.hideArchived}
            label="Hide Archived"
            onChange={(e, checked) =>
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.hideArchived = checked;
                })
              )
            }
          />
        </Grid>
      )}

      {!isActiveSelected && (
        <Grid item xs={fullFieldBreakpoints}>
          {schoolYearField()}
        </Grid>
      )}

      {props.resource.canAccessRegionFilter &&
        props.resource.displayFilters?.find(
          (x) => x.id === LkStudentDisplayFilterId.Region
        ) && (
          <Grid item xs={fullFieldBreakpoints}>{regionField()}</Grid>
        )}

      {props.resource.canAccessDistrictFilter &&
        props.resource.displayFilters?.find(
          (x) => x.id === LkStudentDisplayFilterId.District
        ) && (
          <Grid item xs={fullFieldBreakpoints}>{districtField()}</Grid>
        )}

      {props.resource.canAccessSchoolFilter && schoolDisplayField && (
        <Grid item xs={fullFieldBreakpoints}>{schoolField()}</Grid>
      )}

      {gradeLevelDisplayField && (
        <Grid item xs={fullFieldBreakpoints}>{gradeLevelsField()}</Grid>
      )}

      <Grid item xs={fullFieldBreakpoints}>{studentGroupsField()}</Grid>

      <Grid item xs={fullFieldBreakpoints}>{duplicateByField()}</Grid>

      {props.resource.displayFilters?.find(
        (x) => x.id === LkStudentDisplayFilterId.Program
      ) && (
          <Grid item xs={fullFieldBreakpoints}>{programField()}</Grid>
        )}

      <Grid item xs={fullFieldBreakpoints}>{textingOptInField()}</Grid>

      {/* <Grid item xs={fullFieldBreakpoints}>
        {serviceCountField()}
      </Grid> */}

      <Grid item xs={fullFieldBreakpoints}>
        <StudentCustomIdsPaster
          value={props.filter.customIds}
          typeId={props.filter.customIdTypeId}
          onChange={(customIdTypeId, customIds) => {
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.customIdTypeId = customIdTypeId;
                draft.customIds = customIds;
              })
            );
          }}
        />
      </Grid>
    </Grid>
  );
};
